import { defineStore } from 'pinia';
import { StripeTerminalRepository } from './repository';
import type { OrderInterface } from '../orders/type';
import { ref } from 'vue';
import { useToast } from 'primevue/usetoast'

export const useStripeTerminal = defineStore('stripeTerminal', () => {
    const stripeTerminalRepository = new StripeTerminalRepository();

    const paymentIntentIdStore = ref<string | null>(null);
    const paymentStatus = ref<string | null>(null);
    const paymentError = ref<any>(null);
    const canRetryPayment = ref<boolean>(false);
    const shouldStopPolling = ref<boolean>(false);

    const toast = useToast()

    const createPaymentIntent = async (amount: number, currency: string = 'eur', order: OrderInterface, construction_site: string, email?: string) => {
        return await stripeTerminalRepository.createPaymentIntent(amount, currency, order, construction_site, email);
    };

    const processPayment = async (paymentIntentId: string) => {
        return await stripeTerminalRepository.processPayment(paymentIntentId);
    };

    const simulatePayment = async (paymentIntentId: string) => {
        return await stripeTerminalRepository.simulatePayment(paymentIntentId);
    };

    const capturePayment = async (paymentIntentId: string) => {
        shouldStopPolling.value = true; // Arrêter la boucle en cours
        return await stripeTerminalRepository.capturePayment(paymentIntentId);
    };

    const simulateTap = async () => {
        return await stripeTerminalRepository.simulateTap();
    };

    // Polling pour confirmation paiement
    const waitForPaymentConfirmation = async (paymentIntentId: string, maxAttempts: number = 50) => {
        let attempts = 0;
        paymentIntentIdStore.value = paymentIntentId;
        paymentStatus.value = 'pending';
        paymentError.value = null;
        canRetryPayment.value = false;
        shouldStopPolling.value = false; // Reset du flag

        return new Promise((resolve, reject) => {
            const checkStatus = async () => {
                try {
                    // Vérifier si on doit arrêter la boucle
                    if (shouldStopPolling.value) {
                        return; // Arrêter silencieusement
                    }

                    attempts++;
                    const response = await stripeTerminalRepository.getPaymentStatus(paymentIntentId);
                    const { status, failed, error, can_retry } = response.data;

                    // Update store states
                    paymentStatus.value = status;
                    paymentError.value = error;
                    canRetryPayment.value = can_retry || false;

                    // if (status === 'succeeded') {
                    if (status === 'requires_capture') {
                        await stripeTerminalRepository.capturePayment(paymentIntentId);
                        resolve(response);
                        paymentIntentIdStore.value = null;
                        paymentStatus.value = null;
                        paymentError.value = null;
                        shouldStopPolling.value = false;
                    } else if (failed || status === 'canceled' || status === 'failed') {
                        // Clear payment intent to stop loader
                        paymentIntentIdStore.value = null;
                        shouldStopPolling.value = false;
                        const errorMessage = error?.message || `Payment ${status}`;
                        reject(new Error(errorMessage));
                    }
                    // else if (status === 'requires_capture') {
                    //     // si le paiement nécessite une capture, on le capture
                    //     await stripeTerminalRepository.capturePayment(paymentIntentId);
                    //     setTimeout(checkStatus, 2000);
                    // } 
                    else if (attempts >= maxAttempts) {
                        // Annule le paiement si trop long
                        await stripeTerminalRepository.cancelPayment(paymentIntentId);
                        paymentIntentIdStore.value = null;
                        paymentStatus.value = 'timeout';
                        shouldStopPolling.value = false;
                        if (toast) {
                            toast.removeAllGroups && toast.removeAllGroups();
                            toast.add({
                                severity: 'warn',
                                summary: 'Délai dépassé',
                                detail: 'Le paiement a pris trop de temps et a été annulé.',
                                life: 5000,
                            });
                        }
                        reject(new Error('Payment timeout'));
                    } else {
                        setTimeout(checkStatus, 2000);
                    }
                } catch (error) {
                    paymentIntentIdStore.value = null;
                    paymentStatus.value = 'error';
                    shouldStopPolling.value = false;
                    reject(error);
                }
            };
            checkStatus();
        });
    };

    const cancelPayment = async (paymentIntentId: string, cancellation_comment?: string) => {
        shouldStopPolling.value = true;
        let res = await stripeTerminalRepository.cancelPayment(paymentIntentId, cancellation_comment);
        if (!res.error) {
            paymentIntentIdStore.value = null;
            paymentStatus.value = null;
            paymentError.value = null;
            canRetryPayment.value = false;
        }
        return res;
    };

    const cancelTerminal = async () => {
        shouldStopPolling.value = true;
        let res = null
        if (paymentIntentIdStore.value) {
            res = await cancelPayment(paymentIntentIdStore.value);
        } else {
            res = await stripeTerminalRepository.cancelTerminal();
        }
        if (res && !res.error) {
            paymentIntentIdStore.value = null;
            paymentStatus.value = null;
            paymentError.value = null;
            canRetryPayment.value = false;
        }
    };

    return {
        paymentIntentIdStore,
        paymentStatus,
        paymentError,
        canRetryPayment,
        shouldStopPolling,
        createPaymentIntent,
        processPayment,
        simulatePayment,
        capturePayment,
        simulateTap,
        waitForPaymentConfirmation,
        cancelPayment,
        cancelTerminal,
    };
}, {
    persist: {
        paths: ['paymentIntentIdStore', 'paymentStatus', 'paymentError', 'canRetryPayment', 'shouldStopPolling'],
    },
})